/**
 * Dynamic Labels and Payment Squares Control
 * بسم الله الرحمن الرحيم
 * This script provides dynamic control for Shopee Labels and Payment Squares
 * based on page context, device type, and theme settings.
 */

(function() {
  'use strict';
  
  // Global configuration
  const DynamicLabelsControl = {
    
    // Initialize the control system
    init: function() {
      this.setupConfiguration();
      this.bindEvents();
      this.updateDisplay();
      this.logInit();
    },
    
    // Setup configuration from theme settings and DOM
    setupConfiguration: function() {
      this.config = {
        // Page detection
        pageType: this.detectPageType(),
        deviceType: this.getDeviceType(),
        
        // Theme settings (these would be populated by Liquid in a real implementation)
        shopeeLabels: {
          enabled: this.getSetting('enable_shopee_labels', true),
          showOnMobile: this.getSetting('shopee_labels_show_mobile', true),
          showOnDesktop: this.getSetting('shopee_labels_show_desktop', true),
          showOnHomepage: this.getSetting('shopee_labels_show_homepage', true),
          showOnCollection: this.getSetting('shopee_labels_show_collection', true),
          showOnProduct: this.getSetting('shopee_labels_show_product', true),
          showOnSearch: this.getSetting('shopee_labels_show_search', true),
          position: this.getSetting('shopee_labels_position', 'bottom-left'),
          size: this.getSetting('shopee_labels_size', 'small')
        },
        
        paymentSquares: {
          enabled: this.getSetting('enable_payment_squares', true),
          showOnMobile: this.getSetting('payment_squares_show_mobile', true),
          showOnDesktop: this.getSetting('payment_squares_show_desktop', true),
          showOnHomepage: this.getSetting('payment_squares_show_homepage', true),
          showOnCollection: this.getSetting('payment_squares_show_collection', true),
          showOnProduct: this.getSetting('payment_squares_show_product', true),
          showOnSearch: this.getSetting('payment_squares_show_search', true),
          position: this.getSetting('payment_squares_position', 'bottom-right'),
          size: this.getSetting('payment_squares_size', 'small')
        }
      };
    },
    
    // Get theme setting with fallback
    getSetting: function(key, fallback) {
      // In a real Shopify theme, this would be populated by Liquid
      // For now, we'll check data attributes or use fallbacks
      const element = document.querySelector('[data-' + key.replace(/_/g, '-') + ']');
      if (element) {
        const value = element.getAttribute('data-' + key.replace(/_/g, '-'));
        if (value === 'true') return true;
        if (value === 'false') return false;
        return value;
      }
      return fallback;
    },
    
    // Detect current page type
    detectPageType: function() {
      // Check meta tag first
      const pageTypeMeta = document.querySelector('meta[name="page-type"]');
      if (pageTypeMeta) {
        return pageTypeMeta.getAttribute('content');
      }
      
      // Fallback to URL analysis
      const pathname = window.location.pathname;
      
      if (pathname === '/' || pathname === '/ar' || pathname === '/en') {
        return 'homepage';
      }
      
      if (pathname.includes('/collections/')) {
        return 'collection';
      }
      
      if (pathname.includes('/products/')) {
        return 'product';
      }
      
      if (pathname.includes('/search')) {
        return 'search';
      }
      
      // Check body classes as fallback
      const body = document.body;
      if (body.classList.contains('template-index')) return 'homepage';
      if (body.classList.contains('template-collection')) return 'collection';
      if (body.classList.contains('template-product')) return 'product';
      if (body.classList.contains('template-search')) return 'search';
      
      return 'other';
    },
    
    // Get current device type
    getDeviceType: function() {
      return window.innerWidth < 768 ? 'mobile' : 'desktop';
    },
    
    // Check if element should be displayed
    shouldShowElement: function(elementType) {
      const settings = elementType === 'shopee' ? this.config.shopeeLabels : this.config.paymentSquares;
      
      if (!settings.enabled) return false;
      
      // Check device type
      if (this.config.deviceType === 'mobile' && !settings.showOnMobile) return false;
      if (this.config.deviceType === 'desktop' && !settings.showOnDesktop) return false;
      
      // Check page type
      switch (this.config.pageType) {
        case 'homepage':
          return settings.showOnHomepage;
        case 'collection':
          return settings.showOnCollection;
        case 'product':
          return settings.showOnProduct;
        case 'search':
          return settings.showOnSearch;
        default:
          return true;
      }
    },
    
    // Update display of all elements
    updateDisplay: function() {
      this.updateShopeeLabels();
      this.updatePaymentSquares();
      this.updatePageClasses();
    },
    
    // Update Shopee Labels visibility
    updateShopeeLabels: function() {
      const labels = document.querySelectorAll('.t4s-shopee-labels');
      const shouldShow = this.shouldShowElement('shopee');
      
      labels.forEach(label => {
        if (shouldShow) {
          label.style.display = '';
          label.classList.add('dynamic-visible');
          label.classList.remove('dynamic-hidden');
        } else {
          label.style.display = 'none';
          label.classList.add('dynamic-hidden');
          label.classList.remove('dynamic-visible');
        }
      });
      
      console.log(`Shopee Labels: ${shouldShow ? 'Shown' : 'Hidden'} (${labels.length} elements)`);
    },
    
    // Update Payment Squares visibility
    updatePaymentSquares: function() {
      const squares = document.querySelectorAll('.t4s-payment-squares');
      const shouldShow = this.shouldShowElement('payment');
      
      squares.forEach(square => {
        if (shouldShow) {
          square.style.display = '';
          square.classList.add('dynamic-visible');
          square.classList.remove('dynamic-hidden');
        } else {
          square.style.display = 'none';
          square.classList.add('dynamic-hidden');
          square.classList.remove('dynamic-visible');
        }
      });
      
      console.log(`Payment Squares: ${shouldShow ? 'Shown' : 'Hidden'} (${squares.length} elements)`);
    },
    
    // Update page-specific classes
    updatePageClasses: function() {
      const body = document.body;
      
      // Remove existing dynamic classes
      body.classList.remove('dynamic-homepage', 'dynamic-collection', 'dynamic-product', 'dynamic-search', 'dynamic-mobile', 'dynamic-desktop');
      
      // Add current context classes
      body.classList.add('dynamic-' + this.config.pageType);
      body.classList.add('dynamic-' + this.config.deviceType);
      
      // Add data attributes for CSS targeting
      body.setAttribute('data-dynamic-page', this.config.pageType);
      body.setAttribute('data-dynamic-device', this.config.deviceType);
    },
    
    // Bind events
    bindEvents: function() {
      // Handle window resize
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const newDeviceType = this.getDeviceType();
          if (newDeviceType !== this.config.deviceType) {
            this.config.deviceType = newDeviceType;
            this.updateDisplay();
            console.log('Device type changed to:', newDeviceType);
          }
        }, 250);
      });
      
      // Handle page navigation (for SPAs or AJAX navigation)
      window.addEventListener('popstate', () => {
        setTimeout(() => {
          const newPageType = this.detectPageType();
          if (newPageType !== this.config.pageType) {
            this.config.pageType = newPageType;
            this.updateDisplay();
            console.log('Page type changed to:', newPageType);
          }
        }, 100);
      });
      
      // Handle theme customizer changes (if in preview mode)
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', () => {
          setTimeout(() => {
            this.setupConfiguration();
            this.updateDisplay();
          }, 100);
        });
      }
    },
    
    // Advanced positioning adjustments
    adjustPositioning: function() {
      const pageType = this.config.pageType;
      const deviceType = this.config.deviceType;
      
      // Apply page-specific positioning
      this.applyCustomPositioning('.t4s-shopee-labels', pageType, deviceType);
      this.applyCustomPositioning('.t4s-payment-squares', pageType, deviceType);
    },
    
    // Apply custom positioning rules
    applyCustomPositioning: function(selector, pageType, deviceType) {
      const elements = document.querySelectorAll(selector);
      
      elements.forEach(element => {
        // Reset custom positioning
        element.style.top = '';
        element.style.right = '';
        element.style.bottom = '';
        element.style.left = '';
        
        // Apply page-specific adjustments
        const adjustment = this.getPositionAdjustment(selector, pageType, deviceType);
        if (adjustment) {
          Object.assign(element.style, adjustment);
        }
      });
    },
    
    // Get position adjustment for specific context
    getPositionAdjustment: function(selector, pageType, deviceType) {
      // This would be configured based on theme settings
      const adjustments = {
        '.t4s-shopee-labels': {
          'product': {
            'desktop': { top: '12px' },
            'mobile': { top: '8px' }
          },
          'collection': {
            'desktop': { top: '8px' },
            'mobile': { top: '4px' }
          }
        },
        '.t4s-payment-squares': {
          'product': {
            'desktop': { bottom: '12px' },
            'mobile': { bottom: '8px' }
          }
        }
      };
      
      return adjustments[selector] && adjustments[selector][pageType] && adjustments[selector][pageType][deviceType];
    },
    
    // Performance optimization: Debounced update
    debouncedUpdate: function() {
      if (this.updateTimer) {
        clearTimeout(this.updateTimer);
      }
      
      this.updateTimer = setTimeout(() => {
        this.updateDisplay();
        this.adjustPositioning();
      }, 100);
    },
    
    // Logging for debugging
    logInit: function() {
      console.log('🏷️ Dynamic Labels Control Initialized');
      console.log('📄 Page Type:', this.config.pageType);
      console.log('📱 Device Type:', this.config.deviceType);
      console.log('⚙️ Shopee Labels:', this.config.shopeeLabels.enabled ? 'Enabled' : 'Disabled');
      console.log('💳 Payment Squares:', this.config.paymentSquares.enabled ? 'Enabled' : 'Disabled');
    },
    
    // Public API for manual control
    api: {
      refresh: function() {
        DynamicLabelsControl.updateDisplay();
      },
      
      show: function(elementType) {
        const selector = elementType === 'shopee' ? '.t4s-shopee-labels' : '.t4s-payment-squares';
        document.querySelectorAll(selector).forEach(el => {
          el.style.display = '';
        });
      },
      
      hide: function(elementType) {
        const selector = elementType === 'shopee' ? '.t4s-shopee-labels' : '.t4s-payment-squares';
        document.querySelectorAll(selector).forEach(el => {
          el.style.display = 'none';
        });
      },
      
      getConfig: function() {
        return DynamicLabelsControl.config;
      }
    }
  };
  
  // Initialize when DOM is ready
  function initWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        DynamicLabelsControl.init();
      });
    } else {
      DynamicLabelsControl.init();
    }
  }
  
  // Expose API globally
  window.DynamicLabelsControl = DynamicLabelsControl.api;
  
  // Auto-initialize
  initWhenReady();
  
})();